import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Bot, ChevronDown, Headphones, MessageCircle, Phone } from 'lucide-react';
import CustomerCallHistorySplit from '../CustomerCallHistory/CustomerCallHistorySplit';
import { formatMemberTimestamp } from '../../utils/marketingApi';
import {
  fetchCallLogsByPhone,
  formatCallStatus,
  formatIstDateTime,
  formatOpsCallDateTime,
  getCallStatusTone,
} from '../../utils/callLogsApi';
import s from './MemberLastCallCell.module.scss';

const PAGE_SIZE = 5;

function StatusPill({ status, className = '' }) {
  const tone = getCallStatusTone(status);
  return (
    <span className={`${s.pill} ${s[`pill_${tone}`]} ${className}`.trim()}>
      {formatCallStatus(status)}
    </span>
  );
}

function SourceTag({ source }) {
  if (!source) return null;
  const normalized = String(source).toLowerCase() === 'bot' ? 'bot' : 'ops';
  const label = normalized === 'bot' ? 'Bot call' : 'Ops call';
  return (
    <span className={`${s.source} ${s[`source_${normalized}`]}`} title={label} aria-label={label}>
      {normalized === 'bot' ? <Bot size={12} aria-hidden /> : <Headphones size={12} aria-hidden />}
    </span>
  );
}

function callSource(call) {
  return String(call?.source || '').toLowerCase() === 'bot'
    || String(call?.call_type || '').toLowerCase() === 'marketing_bot'
    ? 'bot'
    : 'ops';
}

function formatCallWhen(call) {
  return callSource(call) === 'bot'
    ? formatIstDateTime(call?.started_at)
    : formatOpsCallDateTime(call?.started_at);
}

function waStatusPillLabel(lastWa) {
  if (!lastWa) return 'Pending';
  const raw = String(lastWa.status_label || '').trim();
  if (raw) return raw;
  const kind = (lastWa.kind_label || 'WhatsApp').toLowerCase();
  return `Sent ${kind}`;
}

function waStatusPillTone(lastWa) {
  const t = String(lastWa?.status_label || lastWa?.kind_label || '').toLowerCase();
  if (t.includes('replied')) return 'success';
  if (t.includes('failed')) return 'danger';
  if (t.includes('read') || t.includes('delivered') || t.includes('sent')) return 'info';
  return 'neutral';
}

function WaStatusPill({ lastWa }) {
  const tone = waStatusPillTone(lastWa);
  return (
    <span className={`${s.pill} ${s[`pill_${tone}`]}`}>
      {waStatusPillLabel(lastWa)}
    </span>
  );
}

function formatWaWhen(lastWa) {
  if (!lastWa?.sent_at) return '';
  return formatMemberTimestamp(lastWa.sent_at);
}

function CallSummaryRow({ call }) {
  if (!call) return null;
  return (
    <span className={`${s.summaryEntry} ${s.summaryCallRow}`}>
      <SourceTag source={callSource(call)} />
      <StatusPill status={call.status} />
      <span className={s.when}>{formatCallWhen(call)}</span>
    </span>
  );
}

function WaSummaryBlock({ lastWa }) {
  return (
    <span
      className={`${s.summaryEntry} ${s.waSummaryEntry}`}
      title={lastWa?.failure_hint || lastWa?.failure_reason || undefined}
    >
      <span className={`${s.source} ${s.source_wa}`} title="WhatsApp" aria-label="WhatsApp">
        <MessageCircle size={12} aria-hidden />
      </span>
      <WaStatusPill lastWa={lastWa} />
      {formatWaWhen(lastWa) ? (
        <span className={s.when}>{formatWaWhen(lastWa)}</span>
      ) : null}
      {lastWa?.failure_hint ? (
        <span className={s.waFailHint}>{lastWa.failure_hint}</span>
      ) : null}
    </span>
  );
}

const MemberLastCallCell = forwardRef(function MemberLastCallCell({
  phone,
  lastCall,
  lastManualCall = null,
  lastBotCall = null,
  history = null,
  lastWa = null,
  leadId = null,
  customerId = null,
  compact = false,
  variant = 'card',
  /** CM: always bot → manual → WA (WA defaults to Pending). */
  fixedContactRows = false,
  /** Follow-up: rich call detail dropdown (ops + bot), flip positioning, open emoji. */
  detailedDropdown = false,
  callSummary = null,
  /** Parent row handles open (click label + cell together). */
  delegateToggleToParent = false,
  /** CM / FU: always fetch merged bot+ops via /call-logs/by-phone (5 per page). */
  serverPaginatedHistory = false,
  /** FU row: anchor portal panel to trigger right edge so it does not spill into remarks. */
  panelAlign = 'start',
}, ref) {
  const [open, setOpen] = useState(false);
  const [placeAbove, setPlaceAbove] = useState(false);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [prefetching, setPrefetching] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [latestCall, setLatestCall] = useState(null);
  const [total, setTotal] = useState(0);
  const [historyVisible, setHistoryVisible] = useState(PAGE_SIZE);
  const [panelStyle, setPanelStyle] = useState(null);
  const rootRef = useRef(null);
  const dropdownRef = useRef(null);

  const providedHistory = serverPaginatedHistory
    ? null
    : (Array.isArray(history) && history.length ? history : null);
  const isTable = variant === 'table';
  const manualSummary = lastManualCall || lastCall;
  const summaryCandidates = [
    manualSummary,
    lastBotCall,
    ...(providedHistory || []),
    latestCall,
    items[0],
  ].filter(Boolean);
  const botCall = lastBotCall || summaryCandidates.find((call) => callSource(call) === 'bot');
  const manualCall = manualSummary || summaryCandidates.find((call) => callSource(call) === 'ops');
  const summaries = ['bot', 'ops']
    .map((source) => (source === 'bot' ? botCall : manualCall))
    .filter(Boolean);
  const usesEmbeddedHistory = Boolean(providedHistory);
  const hasMore = usesEmbeddedHistory
    ? historyVisible < providedHistory.length
    : total > items.length;

  const resetHistory = useCallback(() => {
    setItems([]);
    setTotal(0);
    setPage(0);
    setError('');
    setHistoryVisible(PAGE_SIZE);
  }, []);

  useEffect(() => {
    resetHistory();
    setLatestCall(null);
    setPrefetching(false);
    setOpen(false);
    setPanelStyle(null);
  }, [phone, lastCall?.id, lastWa?.sent_at, resetHistory]);

  useEffect(() => {
    if (!open) setPanelStyle(null);
  }, [open]);

  useEffect(() => {
    if (!phone || lastManualCall || lastCall || lastBotCall || (providedHistory && providedHistory.length)) {
      return undefined;
    }
    let cancelled = false;
    setPrefetching(true);
    fetchCallLogsByPhone(phone, {
      page: 1,
      pageSize: 1,
      leadId,
      customerId,
    })
      .then((data) => {
        if (cancelled) return;
        setLatestCall((data.calls || [])[0] || null);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setPrefetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [phone, lastCall, lastManualCall, lastBotCall, providedHistory, leadId, customerId]);

  const fetchPage = useCallback(async (pageNum, append) => {
    if (!phone || (providedHistory && !serverPaginatedHistory)) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchCallLogsByPhone(phone, {
        page: pageNum,
        pageSize: PAGE_SIZE,
        leadId,
        customerId,
      });
      const calls = data.calls || [];
      setItems((prev) => (append ? [...prev, ...calls] : calls));
      setTotal(data.total || 0);
      setPage(pageNum);
    } catch (err) {
      setError(err.message || 'Failed to load calls');
    } finally {
      setLoading(false);
    }
  }, [phone, providedHistory, serverPaginatedHistory, leadId, customerId]);

  const openDropdown = useCallback(() => {
    if (detailedDropdown) return;
    if (providedHistory) {
      setHistoryVisible(PAGE_SIZE);
      return;
    }
    if (!items.length && phone) {
      fetchPage(1, false);
    }
  }, [detailedDropdown, fetchPage, items.length, phone, providedHistory]);

  const setOpenAndLoad = useCallback((next) => {
    setOpen(next);
    if (next) openDropdown();
  }, [openDropdown]);

  const toggle = useCallback((e) => {
    e?.stopPropagation?.();
    setOpenAndLoad(!open);
  }, [open, setOpenAndLoad]);

  useImperativeHandle(ref, () => ({
    toggleOpen: () => setOpenAndLoad(!open),
    open: () => setOpenAndLoad(true),
    close: () => setOpen(false),
  }), [open, setOpenAndLoad]);

  const handleRootClick = useCallback((e) => {
    if (e.target.closest(`.${s.loadMore}`)) {
      e.stopPropagation();
      return;
    }
    if (e.target.closest('[data-call-dropdown-panel]')) {
      e.stopPropagation();
      return;
    }
    e.stopPropagation();
    setOpenAndLoad(!open);
  }, [open, setOpenAndLoad]);

  const loadMore = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (usesEmbeddedHistory) {
      setHistoryVisible((n) => Math.min(n + PAGE_SIZE, providedHistory.length));
      return;
    }
    fetchPage(page + 1, true);
  }, [usesEmbeddedHistory, providedHistory, fetchPage, page]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (ev) => {
      const t = ev.target;
      if (rootRef.current?.contains(t)) return;
      if (dropdownRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const listItems = usesEmbeddedHistory
    ? providedHistory.slice(0, historyVisible)
    : items;

  const updatePanelPosition = useCallback(() => {
    if (!open || detailedDropdown) return;
    const anchor = rootRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const panelH = dropdownRef.current?.offsetHeight || 320;
    const spaceBelow = window.innerHeight - rect.bottom;
    const above = spaceBelow < panelH + 12 && rect.top > spaceBelow;
    const panelW = Math.min(300, Math.max(260, rect.width));
    let left = panelAlign === 'end' ? rect.right - panelW : rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - panelW - 8));
    setPlaceAbove(above);
    setPanelStyle({
      position: 'fixed',
      left,
      top: above ? rect.top - 4 : rect.bottom + 4,
      width: panelW,
      transform: above ? 'translateY(-100%)' : undefined,
      zIndex: 10130,
    });
  }, [open, detailedDropdown, panelAlign]);

  useLayoutEffect(() => {
    updatePanelPosition();
  }, [updatePanelPosition, listItems.length, loading, items.length, historyVisible]);

  useEffect(() => {
    if (!open || detailedDropdown) return undefined;
    const onScrollOrResize = () => updatePanelPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, detailedDropdown, updatePanelPosition]);

  const rootClass = [
    s.root,
    compact ? s.compact : '',
    open ? s.open : '',
    isTable ? s.table : s.card,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={rootClass}
      ref={rootRef}
      data-member-last-call
      onClick={handleRootClick}
      onKeyDown={delegateToggleToParent ? undefined : (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setOpenAndLoad(!open);
        }
      }}
      role={delegateToggleToParent ? undefined : 'button'}
      tabIndex={delegateToggleToParent ? -1 : 0}
      title="View contact history"
    >
      <div className={s.trigger}>
        {fixedContactRows ? (
          <span
            className={[
              s.summary,
              s.summaryFixed,
              botCall || manualCall ? s.summaryFixed_withCalls : '',
            ].filter(Boolean).join(' ')}
          >
            {botCall ? <CallSummaryRow call={botCall} /> : null}
            {manualCall ? <CallSummaryRow call={manualCall} /> : null}
            <WaSummaryBlock lastWa={lastWa} />
          </span>
        ) : (summaries.length || lastWa != null) ? (
          <span className={s.summary}>
            {summaries.length ? summaries.map((call) => (
              <span className={s.summaryEntry} key={`${callSource(call)}-${call.id || call.started_at}`}>
                <SourceTag source={callSource(call)} />
                <StatusPill status={call.status} />
                <span className={s.when}>{formatCallWhen(call)}</span>
              </span>
            )) : (
              <span className={s.summaryEntry}>
                <span className={s.empty}>No calls</span>
              </span>
            )}
            {lastWa != null ? (
              <span
                className={`${s.summaryEntry} ${s.waSummaryEntry}`}
                title={
                  lastWa?.failure_hint
                    || lastWa?.failure_reason
                    || undefined
                }
              >
                <span className={`${s.source} ${s.source_wa}`} title="WhatsApp" aria-label="WhatsApp">
                  <MessageCircle size={12} aria-hidden />
                </span>
                <WaStatusPill lastWa={lastWa} />
                {formatWaWhen(lastWa) ? (
                  <span className={s.when}>{formatWaWhen(lastWa)}</span>
                ) : null}
                {lastWa?.failure_hint ? (
                  <span className={s.waFailHint}>{lastWa.failure_hint}</span>
                ) : null}
              </span>
            ) : null}
          </span>
        ) : prefetching ? (
          <span className={s.empty}>Loading…</span>
        ) : (
          <span className={s.empty}>No calls</span>
        )}
        {detailedDropdown ? (
          open ? <span className={s.openEmoji} aria-hidden>📋</span> : null
        ) : (
          <ChevronDown size={isTable ? 12 : 14} className={s.chevron} aria-hidden />
        )}
      </div>
      {open && detailedDropdown ? (
        <div
          className={`${s.dropdown} ${s.dropdownRich} ${s.dropdownSplit} ${s.dropdownEmbeddedPanel}`}
          data-call-dropdown-panel
          role="dialog"
        >
          <CustomerCallHistorySplit
            phone={phone}
            customerId={customerId}
            callSummary={callSummary}
            showHeader
          />
        </div>
      ) : null}
      {open && !detailedDropdown && typeof document !== 'undefined'
        ? createPortal(
          <div
            ref={dropdownRef}
            data-call-dropdown-panel
            className={`${s.dropdown} ${s.dropdownPortal}${isTable ? ` ${s.dropdownUp}` : ''}${placeAbove ? ` ${s.dropdownAbove}` : ''}`}
            style={panelStyle || { position: 'fixed', visibility: 'hidden', left: 0, top: 0 }}
            role="dialog"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className={s.dropdownHead}>
              <Phone size={13} aria-hidden />
              <span>Call history</span>
              {listItems.length > 0 ? <span className={s.dropdownCount}>{listItems.length}</span> : null}
            </div>
            <div className={s.dropdownBody}>
              {loading && !listItems.length ? <div className={s.hint}>Loading…</div> : null}
              {error ? <div className={s.error}>{error}</div> : null}
              {listItems.length ? (
                <ul className={s.list}>
                  {listItems.map((c, idx) => (
                    <li key={`${callSource(c)}-${c.id ?? idx}-${c.started_at ?? idx}`}>
                      <StatusPill status={c.status} />
                      <SourceTag source={callSource(c)} />
                      <span className={s.when}>{formatCallWhen(c)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {!loading && !error && page >= 1 && !listItems.length && !usesEmbeddedHistory ? (
                <div className={s.hint}>No call logs for this number</div>
              ) : null}
            </div>
            {hasMore ? (
              <button
                type="button"
                className={s.loadMore}
                disabled={loading}
                onClick={loadMore}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {loading ? 'Loading…' : (
                  usesEmbeddedHistory
                    ? `Load more (${providedHistory.length - historyVisible} left)`
                    : `Load more (${total - items.length} left)`
                )}
              </button>
            ) : null}
            {!hasMore && items.length > 0 && total > PAGE_SIZE && !usesEmbeddedHistory ? (
              <div className={s.endHint}>All {total} calls loaded</div>
            ) : null}
          </div>,
          document.body,
        )
        : null}
    </div>
  );
});

export default MemberLastCallCell;
